import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormTypesData, KeyValueData } from '../../../domains/common.domain';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FacilitiesFormService } from '../facilities-form.service';
import { CommonService } from '../../../services/common.service';
import { AllDropdownKeys, UrlSegments } from '../../jurisdiction.constants';
import { LookupOptionsData } from '../../jurisdiction.domain';
import { AllLabels, InstallationFlowType } from '../../../constants/app.constants';
import { CommunicationService } from '../../../services/communication.service';
import { ErrorService } from '../../../services/error/error.service';
import { EnvironmentService } from '../../../services/environment.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { SiteElectricityInstallationTechnicalDataService } from '../../siteelectricity-installation-technical-data/siteelectricity-installation-technical-data.service';
import { MeterCharacteristicData } from '../facilities-form.domain';
import {
  FacilitiesHiddenDropdownData,
  FacilitiesPowerHiddenFields,
  FacilitiesWaterHiddenFields,
  TechnicianDataHeatingHiddenFields,
} from '../../../interface/config.interface';
import { ValidationsService } from '../../../services/validations.service';

@Component({
  selector: 'app-meter-data',
  templateUrl: './meter-data.component.html',
  styleUrls: ['./meter-data.component.scss'],
})
export class MeterDataComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formType: FormTypesData;
  @Input() isReadOnly: boolean = false;
  @Input() meterData: any = [];
  @Input() chimneySweepData: {
    sk_chim_sweep_confirm: string;
  };
  @Input() selectedSiteConCurrentValue: string = '';
  @Input() selectedMeasuringPointOperator: string = '';

  allUnits: KeyValueData;
  annualConsumptionKey: string;
  annualConsumptionOptions: LookupOptionsData[];
  formSubscription: Subscription;
  meterCommunication: LookupOptionsData[];
  meterCommunicationKey: string;
  meterMount: LookupOptionsData[];
  meterMountKey: string;
  meterSizeKey: string;
  meterType: string[] = [];
  meterTypeKey: string;
  meterDataFG: FormGroup;
  metersObj: FormArray;
  modernMeterKey: string;
  doublerateMeterKey: string;
  swknHeatMeterSizeKey: string;
  transducerSizeKey: string;
  modernMeterType: LookupOptionsData[];
  meterSizeOptions: LookupOptionsData[];
  doublerateMeterOptions: LookupOptionsData[];
  swknHeatMeterSizeOptions: LookupOptionsData[];
  transducerSizeOptions: LookupOptionsData[];
  selectMount: string[];
  showSelectMountAsDropdown: boolean;
  enableSelectMountDropdown: boolean;
  showPluginMountingWarning: boolean;
  showMeterTypeAsDropdown: boolean;
  showModernMeterSubtypeAsDropdown: boolean;
  showFloor: boolean;
  hideMeterCompliance: boolean;
  modernMeterSubtypeList: boolean;
  counterSizeList: boolean;
  typeOfMeters: LookupOptionsData[];
  meterTypeControlUnit: boolean;
  showCommunicationAndMountType: boolean;
  showActualCheckbox: boolean;
  showLabelForTransformerSize: boolean;
  count: number = 1;
  showTypeOfUseAsMeterType: boolean;
  currentFlowType: string;
  selectMountInitialValue: string;
  infoBubblesData: {
    [key: string]: boolean;
  };
  installationFlowType: KeyValueData;
  powerHiddenFields: FacilitiesPowerHiddenFields;
  waterHiddenFields: FacilitiesWaterHiddenFields;
  heatingHiddenFields: TechnicianDataHeatingHiddenFields;
  hiddenDropdownData: FacilitiesHiddenDropdownData;

  constructor(
    private commonService: CommonService,
    private communicationService: CommunicationService,
    public environmentService: EnvironmentService,
    private errorService: ErrorService,
    private facilitiesFormService: FacilitiesFormService,
    private siteElectricityInstallationTechnicalDataService: SiteElectricityInstallationTechnicalDataService,
    private localStorageService: LocalStorageService
  ) {}

  get annualConsumptionUnit(): string {
    return this.formType.key === 'heating'
      ? this.allUnits.volumeHeat
      : this.formType.key === 'water'
      ? this.allUnits.volumeWater
      : this.allUnits.volume;
  }

  ngOnInit(): void {
    this.installationFlowType = InstallationFlowType;
    this.hiddenDropdownData = this.environmentService.facilityHiddenDropdownData;
    this.meterTypeControlUnit = this.environmentService.meterTypeControlUnit;
    this.showCommunicationAndMountType = this.environmentService.showCommunicationAndMountType;
    this.showLabelForTransformerSize = this.environmentService.showLabelForTransformerSize;
    this.showPluginMountingWarning = this.environmentService.showPluginMountingWarning;
    this.showActualCheckbox = this.environmentService.showActualCheckbox;
    this.showTypeOfUseAsMeterType = this.environmentService.showTypeOfUseAsMeterType && this.formType.index === 2;
    this.selectMount = [];
    this.allUnits = AllLabels.units;
    this.powerHiddenFields = this.environmentService.facilityPowerHiddenFields;
    this.waterHiddenFields = this.environmentService.facilityWaterHiddenFields;
    this.heatingHiddenFields = this.environmentService.heatingHiddenFields;
    this.showSelectMountAsDropdown = this.environmentService.showSelectMountAsDropdown;
    this.enableSelectMountDropdown = this.showSelectMountAsDropdown;
    this.showFloor = this.environmentService.showFloor;
    this.modernMeterSubtypeList = this.environmentService.modernMeterSubtypeList;
    this.counterSizeList = this.environmentService.counterSizeList;
    this.hideMeterCompliance = this.environmentService.hideMeterCompliance;
    this.currentFlowType = this.localStorageService.getItem('installation_flow');
    this.showMeterTypeAsDropdown = this.currentFlowType !== InstallationFlowType.SiteElectricityMeterdata;
    this.showModernMeterSubtypeAsDropdown = this.currentFlowType !== InstallationFlowType.SiteElectricityMeterdata;
    this.infoBubblesData = this.environmentService.getInfoBubblesData('facilitiesData') || null;
    this.prepareDropdownData();
    this.prepareForm();
    this.meterData.forEach((data, index) => {
      this.prepareMeterCharacteristicData(data.counterSize, index);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.environmentService.isMeterDataDependentOnOperator &&
      changes?.selectedMeasuringPointOperator &&
      !changes.selectedMeasuringPointOperator.firstChange
    ) {
      this.metersObj.controls.forEach((item, index) => {
        this.msbCheckBoxField(index);
      });
    }
    if (
      (changes?.formType && !changes.formType.firstChange) ||
      (changes?.meterData && !changes.meterData.firstChange) ||
      (changes?.chimneySweepData && !changes.chimneySweepData.firstChange)
    ) {
      this.prepareDropdownData();
      this.prepareForm();
    }
    if (changes?.selectedSiteConCurrentValue && !changes.selectedSiteConCurrentValue.firstChange) {
      this.evaluateFormFields();
    }
  }

  ngOnDestroy(): void {
    this.meterDataFG = null;
    this.metersObj = null;
    this.meterData = [];
    this.formSubscription.unsubscribe();
  }

  prepareMeterCharacteristicData(meterSize: string, index: number) {
    if (this.environmentService.showMeterCharacteristicDetails) {
      const meterCharacteristicFields = [
        { key: 'meterThread', value: 'meter_thread' },
        { key: 'meterFlange', value: 'meter_flange' },
        { key: 'overAllLength', value: 'meter_length' },
        { key: 'sensorLength', value: 'sensor_length' },
        { key: 'sensorThread', value: 'sensor_thread' },
      ];
      if (
        (this.currentFlowType === InstallationFlowType.NewInstallation ||
          this.currentFlowType === InstallationFlowType.UpdateInstallation) &&
        this.formType.key === 'heating' &&
        this.heatingHiddenFields.meterCharacteristicFields
      ) {
        this.communicationService.getMeterCharacteristicDetails(meterSize).subscribe(
          data => {
            this.errorService.throwErrorIfFailure(data, true);
            const meterCharacteristicData: MeterCharacteristicData = data.metercharacteristic[0];
            for (const characteristicFieldsObject of meterCharacteristicFields) {
              const fieldKey = characteristicFieldsObject.key;
              const fieldValue = meterCharacteristicData && meterCharacteristicData[characteristicFieldsObject.value];
              (this.metersObj.controls[index] as FormGroup).addControl(fieldKey, new FormControl(fieldValue));
              (this.metersObj.controls[index] as FormGroup).controls[fieldKey].setValue(fieldValue);
            }
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      }
    }
  }

  prepareDropdownData(): void {
    if (this.formType) {
      switch (this.formType.index) {
        case 0:
          this.meterTypeKey = AllDropdownKeys.PowerMeterType;
          break;
        case 1:
          this.meterTypeKey = AllDropdownKeys.GasMeterType;
          this.meterSizeKey = AllDropdownKeys.GasMeterSize;
          break;
        case 2:
          this.meterTypeKey =
            this.currentFlowType === InstallationFlowType.NewInstallation && this.environmentService.showDropdownsForNewInstallation
              ? AllDropdownKeys.WaterMeterTypeNew
              : AllDropdownKeys.WaterMeterType;
          this.meterSizeKey = AllDropdownKeys.WaterMeterSize;
          break;
        case 3:
          this.meterTypeKey = AllDropdownKeys.HeatingMeterType;
          this.meterSizeKey = AllDropdownKeys.HeatingMeterSize;
          break;
      }
      this.typeOfMeters = this.commonService.getDropdownData(this.meterTypeKey);

      if (!this.environmentService.showAdditionalMeterType) {
        this.typeOfMeters = this.typeOfMeters.filter(x => x.value !== 'bidirectional_unit');
      }
    } else {
      this.typeOfMeters = [];
    }

    this.meterCommunicationKey = AllDropdownKeys.Communication;
    this.meterMountKey = AllDropdownKeys.MeterMount;
    this.modernMeterKey = AllDropdownKeys.ModernMeterSubtype;
    this.doublerateMeterKey = AllDropdownKeys.DoublerateMeterSubtype;
    this.swknHeatMeterSizeKey = AllDropdownKeys.SwknHeatMeterSize;
    this.transducerSizeKey = AllDropdownKeys.TransducerSize;
    this.meterMount = this.commonService.getDropdownData(this.meterMountKey);
    this.meterCommunication = this.commonService.getDropdownData(this.meterCommunicationKey);
    this.modernMeterType = this.commonService.getDropdownData(this.modernMeterKey);
    this.annualConsumptionKey = AllDropdownKeys.ExpectedAnnualConsumption;
    this.annualConsumptionOptions = this.commonService.getDropdownData(this.annualConsumptionKey);
    this.meterSizeOptions = this.commonService.getDropdownData(this.meterSizeKey);
    this.doublerateMeterOptions = this.commonService.getDropdownData(this.doublerateMeterKey);
    this.swknHeatMeterSizeOptions = this.commonService.getDropdownData(this.swknHeatMeterSizeKey);
    this.transducerSizeOptions = this.commonService.getDropdownData(this.transducerSizeKey);
    this.modifyDropdownCollections();
    // this.meterMount.forEach((meter) => {
    //   meter.inactive = true;
    // });
  }

  showAmount(): boolean {
    if (!this.environmentService.showAmount) {
      return !(this.currentFlowType === InstallationFlowType.NewInstallation && this.formType.key === 'heating');
    } else {
      return true;
    }
  }

  prepareForm(): void {
    this.meterDataFG = new FormGroup({
      meters: new FormArray([]),
    });

    if (this.formType.index === 1 && this.environmentService.showChimneySweepConfirmationSection) {
      this.meterDataFG.addControl(
        'chimneySweepConfirm',
        new FormControl({
          value: this.chimneySweepData?.sk_chim_sweep_confirm || 'conf_1',
          disabled: this.isReadOnly,
        })
      );
    }
    this.metersObj = this.meterDataFG.get('meters') as FormArray;
    this.meterType = [];
    this.addMeter();
    const componentService =
      this.currentFlowType === InstallationFlowType.SiteElectricityMeterdata
        ? 'siteElectricityInstallationTechnicalDataService'
        : 'facilitiesFormService';

    this[componentService].registerForComponentFormUpdate.next({
      component: 'meterData',
      formData: this.meterDataFG.get('meters'),
    });

    this.formSubscription = this.meterDataFG.valueChanges.subscribe(() => {
      this[componentService].registerForComponentFormUpdate.next({
        component: 'meterData',
        formData: this.meterDataFG.get('meters'),
      });
    });
  }

  addMeter(newMeter: boolean = false, data: any = null): void {
    if (!newMeter && this.meterData && this.meterData.length) {
      this.meterData.forEach((dataObj, index) => {
        this.addForm(dataObj, index);
      });
    } else {
      this.addForm(data, this.metersObj.controls.length);
    }
  }

  addForm(data?: any, index?: number): void {
    if (data) {
      this.meterType.push(data.meterType);
      this.selectMount.push(data.selectMount);
    }
    this.enableSelectMountDropdown = !this.showSelectMountAsDropdown ? data?.meterType === 'modern_3phase' : this.showSelectMountAsDropdown;
    this.selectMountInitialValue = this.enableSelectMountDropdown
      ? data?.selectMount || ''
      : this.meterMount.filter(item => item.value === 'three_point_mounting')[0]?.value;

    let formGroup: FormGroup;
    if (this.currentFlowType === InstallationFlowType.SiteElectricityMeterdata) {
      formGroup = new FormGroup({
        meterId: new FormControl(data?.meterId || ''),
        installationId: new FormControl(data?.installationId || ''),
        selectMount: new FormControl(this.selectMountInitialValue, [Validators.required]),
      });

      this.metersObj.push(formGroup);
      this.evaluateFormFields(data, index);
      if (this.metersObj.value.length) {
        const meterObj = this.metersObj.value[this.metersObj.value.length - 1];
        this.meterType[this.metersObj.value.length - 1] = meterObj.meterType;
        this.selectMount[this.metersObj.value.length - 1] = meterObj.selectMount;
        formGroup.patchValue(meterObj);
      }

      if (this.formType?.index === 0 && this.currentFlowType === InstallationFlowType.SiteElectricityMeterdata) {
        if (!this.showSelectMountAsDropdown) {
          (this.metersObj.controls[index] as FormGroup).controls.selectMount.setValue('three_point_mounting');
        }
        this.selectMount[index] = (this.metersObj.controls[index] as FormGroup).controls.selectMount.value;
      }
    } else {
      formGroup = new FormGroup({
        meterId: new FormControl(data?.meterId || ''),
        installationId: new FormControl(data?.installationId || ''),
        meterType: new FormControl(data?.meterType || '', [Validators.required]),
        amount: new FormControl(data?.amount || ''),
        swknFloor: new FormControl(data?.swknFloor || ''),
        selectMount: new FormControl(this.selectMountInitialValue),
        annualConsumption: new FormControl(data?.annualConsumption || ''),
        selectComm: new FormControl(data?.selectComm || ''),
        transformerFactor: new FormControl(data?.transformerFactor || ''),
        modernMeterSubtype: new FormControl(data?.modernMeterSubtype || ''),
        remoteReadoutCheckbox: new FormControl(data?.remoteReadoutCheckbox || false),
      });
      if (this.formType.index === 2 && !this.showSelectMountAsDropdown && this.currentFlowType === InstallationFlowType.NewInstallation) {
        formGroup.controls.selectMount.reset();
        formGroup.controls.selectMount.clearValidators();
      }
      if (
        (this.formType.index === 0 || this.environmentService.showOldMeterField) &&
        this.currentFlowType === InstallationFlowType.UpdateInstallation &&
        !this.environmentService.showOldMeterSection
      ) {
        formGroup.addControl('oldMeterNumber', new FormControl(data?.oldMeterNumber || '', Validators.required));
      }
      this.metersObj.push(formGroup);

      if (this.metersObj.value.length && this.showFloor && !data) {
        const meterObj = this.metersObj.value[this.metersObj.value.length - 1];
        this.meterType[this.metersObj.value.length - 1] = meterObj.meterType;
        this.selectMount[this.metersObj.value.length - 1] = meterObj.selectMount;
        formGroup.patchValue(meterObj);
        formGroup.controls.meterId.setValue('');
        formGroup.controls.installationId.setValue('');
      }
      if (
        !this.waterHiddenFields?.alignment ||
        (this.waterHiddenFields?.alignment && this.currentFlowType !== InstallationFlowType.NewInstallation && this.formType.index !== 2)
      ) {
        formGroup.addControl('alignment', new FormControl({ value: data?.alignment || 'horizontal', disabled: this.isReadOnly }));
      }
      if (
        !this.waterHiddenFields?.annualConsumptionForecast ||
        (this.waterHiddenFields?.annualConsumptionForecast &&
          this.currentFlowType !== InstallationFlowType.NewInstallation &&
          this.currentFlowType !== InstallationFlowType.MainsConnection &&
          this.currentFlowType !== InstallationFlowType.UpdateInstallation &&
          this.formType.index !== 2)
      ) {
        formGroup.addControl('annualConsumptionForecast', new FormControl(data?.annualConsumptionForecast || ''));
      }
      if (
        !this.waterHiddenFields.meterAmount ||
        (this.waterHiddenFields.meterAmount && this.currentFlowType !== InstallationFlowType.NewInstallation && this.formType.index !== 2)
      ) {
        formGroup.addControl('amount', new FormControl(data?.amount || ''));
      }
      if (
        !this.waterHiddenFields.swknRemoteReadoutCheckbox &&
        this.currentFlowType === InstallationFlowType.NewInstallation &&
        this.formType.index === 2
      ) {
        formGroup.addControl('swknRemoteReadoutCheckbox', new FormControl(data?.swknRemoteReadoutCheckbox || false));
      }

      //powerHiddenFields
      if (!this.powerHiddenFields.providedByCustomerCheckbox) {
        formGroup.addControl('providedByCustomerCheckbox', new FormControl(data?.providedByCustomerCheckbox || false));
      }
      if (!this.powerHiddenFields.customerName) {
        formGroup.addControl('customerName', new FormControl(data?.customerName || ''));
      }
      if (!this.powerHiddenFields.providedByMsbCheckbox) {
        formGroup.addControl('providedByMsbCheckbox', new FormControl(data?.providedByMsbCheckbox || false));
      }
      if (!this.powerHiddenFields.msbName) {
        formGroup.addControl('msbName', new FormControl(data?.msbName || ''));
      }
      if (
        !this.waterHiddenFields?.alignment ||
        (this.waterHiddenFields?.alignment && this.currentFlowType !== InstallationFlowType.NewInstallation && this.formType.index !== 2)
      ) {
        formGroup.addControl('alignment', new FormControl({ value: data?.alignment || 'horizontal', disabled: this.isReadOnly }));
      }
      if (
        !this.waterHiddenFields?.annualConsumptionForecast ||
        (this.waterHiddenFields?.annualConsumptionForecast &&
          this.currentFlowType !== InstallationFlowType.NewInstallation &&
          this.currentFlowType !== InstallationFlowType.MainsConnection &&
          this.currentFlowType !== InstallationFlowType.UpdateInstallation &&
          this.formType.index !== 2)
      ) {
        formGroup.addControl('annualConsumptionForecast', new FormControl(data?.annualConsumptionForecast || ''));
      }
      if (
        !this.waterHiddenFields.meterAmount ||
        (this.waterHiddenFields.meterAmount && this.currentFlowType !== InstallationFlowType.NewInstallation && this.formType.index !== 2)
      ) {
        formGroup.addControl('amount', new FormControl(data?.amount || ''));
      }
      if (
        !this.waterHiddenFields.swknRemoteReadoutCheckbox &&
        this.currentFlowType === InstallationFlowType.NewInstallation &&
        this.formType.index === 2
      ) {
        formGroup.addControl('swknRemoteReadoutCheckbox', new FormControl(data?.swknRemoteReadoutCheckbox || false));
      }
      if (!this.powerHiddenFields.customerName) {
        formGroup.addControl('customerName', new FormControl(data?.customerName || ''));
      }

      if (this.formType.index === 0 && this.environmentService.showControlUnitAvailableCheckbox) {
        formGroup.addControl('controlUnitAvailableCheckbox', new FormControl(data?.controlUnitAvailableCheckbox || false));
      }

      if (this.formType.index !== 0) {
        formGroup.addControl('counterSize', new FormControl((data && data.counterSize) || ''));
        formGroup.addControl('otherType', new FormControl((data && data.otherType) || ''));
      }

      if (this.formType.index !== 1 && this.meterTypeControlUnit) {
        if (this.showFloor) {
          formGroup.controls.swknFloor.setValidators([Validators.required]);
          formGroup.controls.swknFloor.updateValueAndValidity();
        } else if (!this.environmentService.copyExistingTechnicalDataConfiguration) {
          formGroup.controls.amount.setValidators([Validators.required]);
          formGroup.controls.amount.updateValueAndValidity();
        }
      }

      if (this.formType.index === 2 && !this.hideMeterCompliance) {
        formGroup.addControl(
          'meterCompliance',
          new FormControl({ value: (data && data.meterCompliance) || 'din1988', disabled: this.isReadOnly })
        );
      } else if (this.formType.index === 3 && this.environmentService.showRequestedTotalPower) {
        formGroup.addControl('requestedTotalPower', new FormControl((data && data.requestedTotalPower) || ''));
      }
      if (this.environmentService.showRlmMeasurement) {
        formGroup.addControl('rlmMeasurement', new FormControl((data && data.rlmMeasurement) || false));
      }
      this.handleFormControl(data?.meterType, index);
      if (data) {
        this.addValidator(index, data);
        // this.checkForMeterMount(index);
      }
    }
  }

  customerCheckbox(event: any, i) {
    if (this.showActualCheckbox) {
      if (!event.checked) {
        (this.metersObj.controls[i] as FormGroup).controls?.customerName?.clearValidators();
      } else {
        (this.metersObj.controls[i] as FormGroup).controls?.customerName?.setValidators(Validators.required);
      }
      (this.metersObj.controls[i] as FormGroup).controls?.customerName?.updateValueAndValidity();
    }
  }

  msbCheckbox(event: any, i) {
    if (this.showActualCheckbox) {
      if (!event.checked) {
        (this.metersObj.controls[i] as FormGroup).controls.msbName?.clearValidators();
      } else {
        (this.metersObj.controls[i] as FormGroup).controls.msbName?.setValidators(Validators.required);
      }
      (this.metersObj.controls[i] as FormGroup).controls.msbName?.updateValueAndValidity();
    }
  }

  selectMeterType(data: any, index: number): void {
    this.meterType[index] = data.value;
    this.enableSelectMountDropdown = !(this.showSelectMountAsDropdown || this.environmentService.showOnly3PointMounting)
      ? data.value === 'modern_3phase'
      : this.showSelectMountAsDropdown;
    this.addValidator(index);
    this.handleFormControl(data?.value, index);
  }

  private handleFormControl(meterType: string, index: number): void {
    if (
      this.waterHiddenFields.meterAmount &&
      this.currentFlowType === InstallationFlowType.UpdateInstallation &&
      ((this.formType.index === 2 && meterType === 'remote') ||
        (this.formType.index === 3 && (meterType === 'remote' || meterType === 'transducer')))
    ) {
      (this.metersObj.controls[index] as FormGroup).removeControl('amount');
    }
    if (this.environmentService.isMeterDataDependentOnOperator) {
      const isMeterTypeTransducer =
        (this.currentFlowType === InstallationFlowType.NewInstallation ||
          this.currentFlowType === InstallationFlowType.UpdateInstallation) &&
        this.formType.key === 'power' &&
        meterType === 'transducer';
      if (isMeterTypeTransducer) {
        (this.metersObj.controls[index] as FormGroup).removeControl('selectMount');
        (this.metersObj.controls[index] as FormGroup).removeControl('providedByCustomerCheckbox');
        (this.metersObj.controls[index] as FormGroup).removeControl('customerName');
        this.msbCheckBoxField(index);
        (this.metersObj.controls[index] as FormGroup).removeControl('transformerFactor');
        (this.metersObj.controls[index] as FormGroup).addControl(
          'transducerSize',
          new FormControl(this.meterData[index]?.transducerSize || '', [Validators.required])
        );
      } else {
        (this.metersObj.controls[index] as FormGroup).addControl(
          'selectMount',
          new FormControl(this.selectMountInitialValue || '', [Validators.required])
        );
        (this.metersObj.controls[index] as FormGroup).removeControl('transducerSize');
      }
    }

    if (
      (this.environmentService.showMeterLocation &&
        (this.formType.index === 1 || this.environmentService.showMeterLocationForEveryComodity) &&
        (this.currentFlowType === InstallationFlowType.UpdateInstallation ||
          this.currentFlowType === InstallationFlowType.NewInstallation) &&
        meterType) ||
      (this.formType.key === 'water' && this.environmentService.hideAddWaterTapOption)
    ) {
      (this.metersObj.controls[index] as FormGroup).addControl(
        'location',
        new FormControl(this.meterData[index]?.location || '', Validators.required)
      );
    } else {
      (this.metersObj.controls[index] as FormGroup).removeControl('location');
    }

    if (
      this.powerHiddenFields.mount &&
      (this.currentFlowType === InstallationFlowType.NewInstallation || this.currentFlowType === InstallationFlowType.UpdateInstallation) &&
      meterType === 'profile'
    ) {
      (this.metersObj.controls[index] as FormGroup).removeControl('selectMount');
    }
  }

  msbCheckBoxField(index) {
    if (this.selectedMeasuringPointOperator === 'customer') {
      (this.metersObj.controls[index] as FormGroup).removeControl('providedByMsbCheckbox');
      (this.metersObj.controls[index] as FormGroup).removeControl('msbName');
    } else {
      (this.metersObj.controls[index] as FormGroup).addControl('providedByMsbCheckbox', new FormControl(''));
      (this.metersObj.controls[index] as FormGroup).addControl('msbName', new FormControl(''));
    }
  }

  removeMeter(id: number): void {
    const meterId = this.metersObj.value[id].meterId;
    let deleteUrl: string;
    switch (this.formType.index) {
      case 0:
        deleteUrl = UrlSegments.UpdatePowerMeter;
        break;
      case 1:
        deleteUrl = UrlSegments.UpdateGasMeter;
        break;
      case 2:
        deleteUrl = UrlSegments.UpdateWaterMeter;
        break;
      case 3:
        deleteUrl = UrlSegments.UpdateHeatingMeter;
        break;
    }
    if (meterId) {
      this.communicationService.deleteData(`${deleteUrl}=${meterId}`).subscribe(
        response => {
          this.errorService.throwErrorIfFailure(response, true);
          this.metersObj.removeAt(id);
          this.meterType.splice(id, 1);
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    } else {
      this.metersObj.removeAt(id);
      this.meterType.splice(id, 1);
    }
  }

  checkSelectedMeterSize(index: number): boolean {
    if ((this.metersObj.controls[index] as FormGroup).controls.counterSize.value === 'other') {
      (this.metersObj.controls[index] as FormGroup).controls.otherType.setValidators([Validators.required]);
    } else {
      (this.metersObj.controls[index] as FormGroup).controls.otherType.reset();
      (this.metersObj.controls[index] as FormGroup).controls.otherType.clearValidators();
    }
    (this.metersObj.controls[index] as FormGroup).controls.otherType.updateValueAndValidity();

    return (this.metersObj.controls[index] as FormGroup).controls.counterSize.value === 'other';
  }

  /*checkForMeterMount(index: number): void {
   this.meterMount.forEach((meter) => {
   meter.inactive = !((this.meterDataFG.get('meters') as FormArray).controls[index]
   .get('modernMeterSubtype').value === 'single_rate_meter'
   && this.formType.key === 'power' && meter.value === 'three_point_mounting');
   });
   }*/

  addMultipleMeterData() {
    const index = this.metersObj.controls.length - 1;
    const meterData = { ...(this.metersObj.controls[index] as FormGroup).value };
    meterData.meterId = '';
    meterData.installationId = '';
    for (let i = 0; i < this.count; i++) {
      this.addMeter(true, meterData);
    }
  }

  caretUp() {
    if (!this.count) {
      this.count = 1;
      return;
    }
    if (this.count >= 1 && this.count < 20) {
      this.count++;
    }
  }

  caretDown() {
    if (!this.count) {
      this.count = 1;
      return;
    }
    if (this.count > 1) {
      this.count--;
    }
  }

  counter(event: any) {
    if (event.key === 'Backspace') {
      return;
    }
    if (this.count <= 0 || this.count >= 21) {
      this.count = null;
    }
  }

  showControlUnitAvailableCheckbox(index: number): boolean {
    return (
      this.metersObj.controls[index].get('controlUnitAvailableCheckbox') &&
      this.formType.key === 'power' &&
      ['modern', 'modern_3phase'].indexOf(this.meterType[index]) > -1
    );
  }

  isFieldRequired(formControl: AbstractControl) {
    return ValidationsService.isFieldRequired(formControl);
  }

  private addValidator(index: number, data?: any): void {
    if (this.formType && this.formType.index === 0) {
      if (this.meterTypeControlUnit || (this.meterType[index] && this.meterType[index] !== 'control_unit')) {
        if (!this.enableSelectMountDropdown) {
          (this.metersObj.controls[index] as FormGroup).controls?.selectMount?.setValue('three_point_mounting');
        }
        this.selectMount[index] = (this.metersObj.controls[index] as FormGroup).controls?.selectMount?.value;
        (this.metersObj.controls[index] as FormGroup).controls?.selectMount?.setValidators([Validators.required]);
        if (!this.showCommunicationAndMountType && this.meterType[index] === 'intelligent') {
          (this.metersObj.controls[index] as FormGroup).controls?.selectMount?.clearValidators();
        }
        if (this.showFloor) {
          (this.metersObj.controls[index] as FormGroup).controls.swknFloor.setValidators([Validators.required]);
        } else if (!this.environmentService.copyExistingTechnicalDataConfiguration) {
          (this.metersObj.controls[index] as FormGroup).controls.amount.setValidators([Validators.required]);
        }
        if (
          !this.environmentService.annualConsumptionNotRequired &&
          !(this.environmentService.annualConsumptionNotRequiredWhenMeterTypeIsProfile && this.meterType[index] === 'profile')
        ) {
          (this.metersObj.controls[index] as FormGroup).controls.annualConsumption.setValidators([Validators.required]);
        } else {
          (this.metersObj.controls[index] as FormGroup).controls.annualConsumption?.clearValidators();
        }
      }
      (this.metersObj.controls[index] as FormGroup).controls.modernMeterSubtype?.reset();
      (this.metersObj.controls[index] as FormGroup).controls.modernMeterSubtype?.clearValidators();
      (this.metersObj.controls[index] as FormGroup).controls.selectComm?.reset();
      (this.metersObj.controls[index] as FormGroup).controls.selectComm?.clearValidators();
      (this.metersObj.controls[index] as FormGroup).controls.transformerFactor?.reset();
      (this.metersObj.controls[index] as FormGroup).controls.transformerFactor?.clearValidators();
      (this.metersObj.controls[index] as FormGroup).controls.customerName?.reset();
      (this.metersObj.controls[index] as FormGroup).controls.customerName?.clearValidators();
      (this.metersObj.controls[index] as FormGroup).controls.msbName?.reset();
      (this.metersObj.controls[index] as FormGroup).controls.msbName?.clearValidators();

      if (!this.meterTypeControlUnit && this.meterType[index] === 'control_unit') {
        if (this.showFloor) {
          (this.metersObj.controls[index] as FormGroup).controls.swknFloor?.reset();
          (this.metersObj.controls[index] as FormGroup).controls.swknFloor?.clearValidators();
        } else {
          (this.metersObj.controls[index] as FormGroup).controls.amount?.reset();
          (this.metersObj.controls[index] as FormGroup).controls.amount?.clearValidators();
        }
        (this.metersObj.controls[index] as FormGroup).controls.selectMount?.clearValidators();
        (this.metersObj.controls[index] as FormGroup).controls.annualConsumption?.reset();
        (this.metersObj.controls[index] as FormGroup).controls.annualConsumption?.clearValidators();
      }

      if (['control_unit', 'modern_ac', 'modern_3phase', 'modern'].indexOf(this.meterType[index]) > -1) {
        if (data) {
          (this.metersObj.controls[index] as FormGroup).controls.modernMeterSubtype.setValue(data.modernMeterSubtype);
        }
        if (this.meterTypeControlUnit) {
          (this.metersObj.controls[index] as FormGroup).controls.modernMeterSubtype.setValidators([Validators.required]);
        }
      } else if (this.meterType[index] === 'intelligent') {
        if (data) {
          (this.metersObj.controls[index] as FormGroup).controls.selectComm.setValue(data.selectComm);
        }
        if (this.showCommunicationAndMountType) {
          (this.metersObj.controls[index] as FormGroup).controls.selectComm.setValidators([Validators.required]);
        } else {
          (this.metersObj.controls[index] as FormGroup).controls.selectComm.clearValidators();
        }
      } else if (this.meterType[index] === 'transducer') {
        if (this.showFloor) {
          (this.metersObj.controls[index] as FormGroup).controls.swknFloor.reset();
          (this.metersObj.controls[index] as FormGroup).controls.swknFloor.clearValidators();
        } else {
          (this.metersObj.controls[index] as FormGroup).controls.amount.reset();
          (this.metersObj.controls[index] as FormGroup).controls.amount.clearValidators();
        }
        if (data) {
          (this.metersObj.controls[index] as FormGroup).controls.transformerFactor?.setValue(data.transformerFactor);
          (this.metersObj.controls[index] as FormGroup).controls.customerName?.setValue(data.customerName);
          (this.metersObj.controls[index] as FormGroup).controls.msbName?.setValue(data.msbName);
        }
        (this.metersObj.controls[index] as FormGroup).controls.transformerFactor?.setValidators([Validators.required]);
        const isValidator = this.showActualCheckbox ? null : Validators.required;
        (this.metersObj.controls[index] as FormGroup).controls.customerName?.setValidators(isValidator);
        (this.metersObj.controls[index] as FormGroup).controls.msbName?.setValidators(isValidator);
      }
      (this.metersObj.controls[index] as FormGroup).controls.annualConsumption?.updateValueAndValidity();
      (this.metersObj.controls[index] as FormGroup).controls.modernMeterSubtype?.updateValueAndValidity();
      (this.metersObj.controls[index] as FormGroup).controls.selectComm?.updateValueAndValidity();
      (this.metersObj.controls[index] as FormGroup).controls.transformerFactor?.updateValueAndValidity();
      (this.metersObj.controls[index] as FormGroup).controls.customerName?.updateValueAndValidity();
      (this.metersObj.controls[index] as FormGroup).controls.msbName?.updateValueAndValidity();
      if (this.showFloor) {
        (this.metersObj.controls[index] as FormGroup).controls.swknFloor?.updateValueAndValidity();
      } else {
        (this.metersObj.controls[index] as FormGroup).controls.amount?.updateValueAndValidity();
      }
      (this.metersObj.controls[index] as FormGroup).controls.selectMount?.updateValueAndValidity();
    }

    if (this.formType && this.formType.index !== 0) {
      const meterType = (this.metersObj.controls[index] as FormGroup).controls.meterType.value;
      if (['transducer', 'remote', 'simple', 'bellows_meter'].indexOf(meterType) > -1) {
        (this.metersObj.controls[index] as FormGroup).controls.counterSize.setValidators([Validators.required]);
      } else {
        (this.metersObj.controls[index] as FormGroup).controls.counterSize.reset();
        (this.metersObj.controls[index] as FormGroup).controls.counterSize.clearValidators();
      }

      if (
        this.formType.key === 'water' &&
        meterType !== 'simple' &&
        meterType !== 'own_water' &&
        !this.environmentService.copyExistingTechnicalDataConfiguration
      ) {
        (this.metersObj.controls[index] as FormGroup).controls.amount?.setValidators([Validators.required]);
      } else if (this.formType.key !== 'heating') {
        (this.metersObj.controls[index] as FormGroup).controls.amount?.reset();
        (this.metersObj.controls[index] as FormGroup).controls.amount?.clearValidators();
      }
      (this.metersObj.controls[index] as FormGroup).controls.amount?.updateValueAndValidity();
      (this.metersObj.controls[index] as FormGroup).controls.counterSize.updateValueAndValidity();
    }
  }

  private evaluateFormFields(data?: any, index?: number) {
    if (!isNaN(index)) {
      this.resetFormFields(this.metersObj.controls[index] as FormGroup, index, data);
    } else if (this.metersObj.controls.length) {
      this.metersObj.controls.forEach((meterItem: FormGroup, i: number) => {
        this.resetFormFields(meterItem, i);
      });
    }
  }

  private resetFormFields(meterItem: FormGroup, index: number, data?: any) {
    if (this.selectedSiteConCurrentValue === 'less_equal_80') {
      meterItem.removeControl('transformerFactor');
      meterItem.removeControl('providedByCustomerCheckbox');
      meterItem.addControl('meterType', new FormControl('modern_3phase', [Validators.required]));
      meterItem.addControl('modernMeterSubtype', new FormControl('single_rate_meter', [Validators.required]));
      meterItem.addControl(
        'annualConsumption',
        new FormControl(
          data?.annualConsumption || '',
          !this.environmentService.annualConsumptionNotRequired &&
          !(this.environmentService.annualConsumptionNotRequiredWhenMeterTypeIsProfile && this.meterType[index] === 'profile')
            ? [Validators.required]
            : []
        )
      );
      this.meterType[index] = meterItem.value.meterType;
    } else if (this.selectedSiteConCurrentValue === 'greater_80') {
      meterItem.removeControl('meterType');
      meterItem.removeControl('modernMeterSubtype');
      meterItem.removeControl('annualConsumption');
      meterItem.addControl('transformerFactor', new FormControl(data?.transformerFactor || '', [Validators.required]));
      meterItem.addControl('providedByCustomerCheckbox', new FormControl(data?.providedByCustomerCheckbox || false, [Validators.required]));
      this.meterType[index] = '';
    }
  }

  private modifyDropdownCollections(): void {
    if (this.hiddenDropdownData?.modernMeterType) {
      this.modernMeterType = this.modernMeterType.filter(sa => sa.value !== 'dual' && sa.value !== 'double_dual');
    }
  }

  isValid(): boolean {
    return this.meterDataFG.valid;
  }
}
